import {
  reactive,
  UnwrapRef,
  ref,
  computed,
  Ref,
  ComputedRef,
  ComponentInternalInstance,
  getCurrentInstance,
} from '@vue/composition-api';
import _, { debounce } from 'lodash';

import i18n, { i18n_t } from '@/locales/i18n';
import extraFileInfoHandler from '@/renderer/components/SpacePage/compositions/extraFileInfoHandler';
import useImgPre from '@/renderer/components/SpacePage/compositions/useImagePreview';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import spaceATM from '@/renderer/scripts/utils/AccessTokenManager/space';
import useSort, { Sort } from '@/renderer/components/SpacePage/compositions/useSort';

export type SearchItem = {
  name: string;
  type: string;
  path: string[];
  creationTime: string;
  modificationTime?: string;
  contentType?: string;
  size?: string;
  crc64?: string;
  ETag?: string;
  highlight?: {
    name?: string[];
    fileContent?: string[];
  };
  metaData?: object;
};
type TableData = {
  list: any[];
  selectedRowKeys: any[];
  selectedList: any[];
  nextMarker: number;
  limit: number;
  hasMore: boolean;
  total: number; // 已搜索到的总数
};

type SearchCondition = {
  modificationTimeStart: string;
  modificationTimeEnd: string;
  minFileSize?: number;
  maxFileSize?: number;
  creators: [];
};

export interface SearchParam {
  keyword: string; // 输入框绑定的搜索关键字
  keywordCache: string; // 真实搜索使用的关键字
  scope: 'global' | 'all' | 'current' | '';
  scopeCache: 'global' | 'all' | 'current' | '';
  types: SMH.SearchFileType[];
  typesCache: SMH.SearchFileType[];
  range: string;
  rangeCache: string;
  time: string;
  timeCache: string;
  size: string;
  sizeCache: string;
  member: any[];
  memberCache: any[];
  members: string;
  originMember: [];
}
interface SearchTypeOption {
  label: string;
  value: string;
  disabled?: boolean;
}
const SearchRangeOptions: SearchTypeOption[] = [
  {
    label: i18n_t('仅文件名'),
    value: 'fileName',
  },
  {
    label: i18n_t('仅文件内容'),
    value: 'fileContents',
  },
  {
    label: i18n_t('全部内容'),
    value: 'all',
  },
];
const SearchTimeOptions: SearchTypeOption[] = [
  {
    label: i18n_t('全部'),
    value: 'all',
  },
  {
    label: i18n_t('一天内'),
    value: '0',
  },
  {
    label: i18n_t('一周内'),
    value: '7',
  },
  {
    label: i18n_t('一个月内'),
    value: '30',
  },
  {
    label: i18n_t('一年内'),
    value: '365',
  },
];
const SearchSizeOptions: SearchTypeOption[] = [
  {
    label: i18n_t('不限'),
    value: 'all',
  },
  {
    label: '0-1MB',
    value: '0-1MB',
  },
  {
    label: '1-5MB',
    value: '1-5MB',
  },
  {
    label: '5-25MB',
    value: '5-25MB',
  },
  {
    label: '25-100MB',
    value: '25-100MB',
  },
  {
    label: '100MB-1GB',
    value: '100MB-1GB',
  },
  {
    label: '1GB+',
    value: '1GB+',
  },
];
const SearchMemberOptions: SearchTypeOption[] = [
  {
    label: i18n_t('不限'),
    value: 'all',
  },
];
type Handler = {
  columns: ComputedRef<Array<any>>;
  SearchRangeOptions: SearchTypeOption[];
  SearchTypeOptions: ComputedRef<SearchTypeOption[]>;
  SearchTimeOptions: SearchTypeOption[];
  SearchSizeOptions: SearchTypeOption[];
  SearchMemberOptions: SearchTypeOption[];
  loading: Ref<boolean>;
  isAccurateSearch: Ref<boolean>;
  tableData: UnwrapRef<TableData>;
  sort: UnwrapRef<Sort>;
  searchParams: UnwrapRef<SearchParam>;
  searchCondition: UnwrapRef<SearchCondition>;
  loadingStatus: Ref<string>;
  methods: {
    initData: () => void;
    pullList: (currPath?: string, searchType?: string) => void;
    onSearch: (currPath?: string, searchType?: string) => void;
    resetSearchCondition: () => void;
    sortChange: (newSort: Sort) => void;
    switchSort: (columnName: string) => void;
  };
};

/**
 * @description 搜索处理器
 * @export
 * @param {boolean} [skipInit] 是否跳过初始化
 * @return {*}  {Handler}
 */
export default function handler(spaceId: Ref<SpaceId>, skipInit?: boolean): Handler {
  const { methods: extraFileInfoMethods } = extraFileInfoHandler(spaceId);
  const useImagePreview = useImgPre();
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const store = vm.$store;
  const searchPreviousRoute = computed(() => store.getters.searchPreviousRoute);
  // 若来自个人空间搜索路由|在个人空间下
  const isPersonalSpace = computed(
    () => store.getters.searchPreviousRoute.name === 'personalSpace' || vm.$route.name === 'personalSpace',
  );
  const isMobileStyle = computed(() => store.getters.isMobileStyle);
  const isAccurateSearch = ref(true); // 是否为精准搜索
  // 只有精准搜索时才进行排序
  const generalColumns = computed(() => [
    {
      colKey: 'name',
      title: i18n_t('文件名'),
      ellipsis: false,
    },
    {
      colKey: 'modifierName',
      title: i18n_t('创建者/更新者'),
      width: 180,
    },
    {
      colKey: 'modificationTime',
      title: 'title-modificationTime',
      titleDesc: i18n_t('修改时间'),
      sortType: 'all',
      sorter: isAccurateSearch.value,
      width: 200,
    },
    {
      colKey: 'size',
      title: 'title-size',
      titleDesc: i18n_t('文件大小'),
      sortType: 'all',
      sorter: isAccurateSearch.value,
      width: 150,
    },
  ]);
  const mobileColumns = [
    {
      colKey: 'mobile-name',
      title: i18n_t('文件名'),
      ellipsis: true,
      width: 200,
    },
    {
      colKey: 'mobile-operate',
      width: 80,
      align: 'right',
    },
  ];
  // 初始化columns
  const columns = computed(() => (isMobileStyle.value ? mobileColumns : generalColumns.value));
  const loading = ref(false);
  const defaultScope = computed(() => (searchPreviousRoute.value.name === 'workbench' ? 'global' : 'all'));
  const defaultSearchParam: SearchParam = {
    keyword: '',
    keywordCache: '',
    scope: defaultScope.value,
    scopeCache: defaultScope.value,
    types: ['all'],
    typesCache: ['all'],
    range: 'fileName',
    rangeCache: 'fileName',
    time: 'all',
    timeCache: 'all',
    size: 'all',
    sizeCache: 'all',
    member: ['all'],
    memberCache: ['all'],
    members: '',
    originMember: [],
  };
  const tableData = reactive<TableData>({
    list: [],
    selectedRowKeys: [],
    selectedList: [],
    nextMarker: 0,
    limit: 20,
    hasMore: false,
    total: 0,
  });

  const {
    innerSort,
    tableSort,
    methods: sortMethods,
  } = useSort('search', {
    sortBy: undefined,
    descending: true,
  });
  const searchParams = reactive<SearchParam>(defaultSearchParam);
  const searchCondition = reactive<SearchCondition>({
    modificationTimeStart: '',
    modificationTimeEnd: '',
    creators: [],
  });
  const SearchTypeOptions = computed(() => {
    const options = [
      {
        label: i18n_t('全部'),
        value: 'all',
      },
      {
        label: i18n_t('文件夹'),
        value: 'dir',
      },
      {
        label: i18n_t('文件'),
        value: 'file',
      },
      {
        label: i18n_t('Word文档'),
        value: 'word',
      },
      {
        label: i18n_t('Excel文档'),
        value: 'excel',
      },
      {
        label: i18n_t('PPT文档'),
        value: 'powerpoint',
      },
      {
        label: i18n_t('PDF文档'),
        value: 'pdf',
      },
      {
        label: i18n_t('纯文本类型'),
        value: 'text',
      },
      {
        label: i18n_t('图片'),
        value: 'image',
      },
      {
        label: i18n_t('视频'),
        value: 'video',
      },
      {
        label: i18n_t('音频'),
        value: 'audio',
      },
    ];
    if (searchParams.types.includes('all')) {
      return options.map(item => ({
        ...item,
        disabled: item.value !== 'all',
      }));
    }
    return options.map(item => ({
      ...item,
      disabled: false,
    }));
  });
  const loadingStatus = ref('');
  const searchScopePath = computed<string>(() => {
    if (['global', 'all'].includes(searchParams.scope)) {
      return '';
    }
    return (searchPreviousRoute.value?.query.path as string) || '';
  });
  const personalSpaceId = ref('');
  const searchSpaceId = computed({
    get() {
      // 如果全局搜索spaceId为空，如果是个人空间每次在拉取前通过spaceATM.getPersonalSpaceIdAsync获取
      if (searchParams.scope === 'global') {
        return '';
      }
      if (isPersonalSpace.value) {
        return personalSpaceId.value;
      }
      return spaceId.value;
    },
    set(val: string) {
      personalSpaceId.value = val;
    },
  });
  const spaceOrgId = computed(() => {
    if (searchParams.scope === 'global') {
      return '';
    }
    // 协作空间的搜索新增spaceOrgId字段
    if (searchPreviousRoute.value.name === 'collaborationSpaceList') {
      return searchPreviousRoute.value.query?.spaceOrgId;
    }
    return '';
  });
  const methods = {
    async autoImagePreView(params: { e: WheelEvent }, rowHeight: number) {
      useImagePreview.debouncedFetchData(params, rowHeight, tableData, tableData.list);
    },
    initData() {
      Object.assign(tableData, {
        list: [],
        selectedRowKeys: [],
        selectedList: [],
        nextMarker: 0,
        limit: 20,
        hasMore: false,
        total: 0,
      });
    },
    async pullList(currPath?: string, searchType?: string) {
      if (loading.value) return;
      if (searchParams.keyword.trim().length > 200) {
        tips.error(i18n_t('搜索关键词过长，请精简后再搜索'));
        return;
      }
      const startTime = new Date().getTime();

      // TODO: 后续要优化成baseInfo里面同步取值
      if (store.getters.hasPersonalSpace) {
        personalSpaceId.value = await spaceATM.getPersonalSpaceIdAsync();
      }
      const scope = typeof currPath === 'string' ? currPath : searchScopePath.value;
      let listCache: SMH.SearchFileData[] = tableData.list;
      const searchConditionClone = _.clone(searchCondition);
      // 避免破坏响应式对象
      if (searchParams.size === 'all') {
        if (searchConditionClone?.minFileSize) {
          delete searchConditionClone?.minFileSize;
        }
        if (searchConditionClone?.maxFileSize) {
          delete searchConditionClone?.maxFileSize;
        }
      } else if (searchParams.size === '1GB+') {
        if (searchConditionClone?.maxFileSize) {
          delete searchConditionClone?.maxFileSize;
        }
      } else if (searchParams.types.length === 1 && searchParams.types[0] === 'dir') {
        delete searchConditionClone?.minFileSize;
        delete searchConditionClone?.maxFileSize;
      }
      const loop = async (isloop?: boolean) => {
        try {
          const params = {
            keyword: searchParams.keyword.trim(),
            scope,
            limit: tableData.limit,
            type: searchParams.types,
            ...searchConditionClone,
            spaceId: searchSpaceId.value,
            spaceOrgId: spaceOrgId.value ? Number(spaceOrgId.value) : undefined,
            accurate: isAccurateSearch.value,
            searchBy: searchParams.range,
            orderBy: innerSort.value.orderBy,
            orderByType: innerSort.value.orderByType,
          };
          loading.value = true;
          if ((searchType === 'scroll' || isloop) && tableData.nextMarker) {
            Object.assign(params, {
              marker: tableData.nextMarker,
            });
          } else {
            tableData.list = [];
          }
          const { data } = await smh.userAPI.createSearchTask(params);
          tableData.hasMore = data.hasMore;
          if (data.hasMore) {
            tableData.nextMarker = data.nextMarker as number;
          }
          const searchContent = data.contents;
          if (searchType === 'scroll' || isloop) {
            listCache.push(...searchContent);
          } else {
            listCache = searchContent;
          }
          // 因为后端返回数据有可能contents为空 但hasMore为true
          if (listCache.length < 10 && tableData.hasMore) {
            loop(true);
          } else {
            tableData.list = listCache.map((item, index) => ({
              ...item,
              id: index,
              isCutOff: false,
            }));
            tableData.total = tableData.list.length;
            // 筛选大小时过滤掉文件夹
            if (searchConditionClone.maxFileSize || searchConditionClone.minFileSize) {
              tableData.list = tableData.list.filter(item => !(item.type === 'dir'));
            }
            useImagePreview.getImagePre(tableData, tableData.list, 0, 20, 20);
            loading.value = false;
            extraFileInfoMethods.getFileExtraRefresh(tableData, { targetList: searchContent });
          }
          reporter.report('list_cost_time', {
            page: 'Search',
            cost: new Date().getTime() - startTime,
            count: tableData.limit,
          });
        } catch (err) {
          loading.value = false;
          tips.error(err as any);
        }
      };
      await loop();
    },
    onSearch(currPath?: string, searchType?: string) {
      // 点击搜索时： 关键字与上次搜索一致 && 搜索条件与上次一致
      const searchParamsCompared: Record<string, string | string[]> = _.cloneDeep(searchParams);
      const searchConditionArr = ['keyword', 'scope', 'types', 'range', 'time', 'size', 'member'];
      const isSame = searchConditionArr.every(key =>
        _.isEqual(searchParamsCompared[key], searchParamsCompared[`${key}Cache`]),
      );
      if (isSame && !isAccurateSearch) return;
      searchConditionArr.forEach(key => {
        searchParamsCompared[`${key}Cache`] = _.cloneDeep(searchParamsCompared[key]);
      });
      Object.assign(searchParams, searchParamsCompared);
      methods.pullList(currPath, searchType);
    },
    onScroll(params: { e: WheelEvent }) {
      methods.autoImagePreView(params, 90);
      const { target } = params.e;
      const { clientHeight, scrollHeight, scrollTop } = target as HTMLElement;
      if (clientHeight + scrollTop >= scrollHeight - 100) {
        if (!loading.value && tableData.hasMore) {
          loadingStatus.value = 'loading';
          methods.scrollGetData();
        }
      }
    },
    scrollGetData: debounce(async () => {
      await methods.pullList('', 'scroll');
      loadingStatus.value = '';
    }, 500),
    resetSearchCondition() {
      Object.assign(searchParams, {
        keyword: '',
        keywordCache: '',
        scope: defaultScope.value,
        scopeCache: defaultScope.value,
        types: ['all'],
        typesCache: ['all'],
        range: 'fileName',
        rangeCache: 'fileName',
        time: 'all',
        timeCache: 'all',
        size: 'all',
        sizeCache: 'all',
        member: ['all'],
        memberCache: ['all'],
        members: '',
        originMember: [],
      });
      Object.assign(searchCondition, {
        modificationTimeStart: '',
        modificationTimeEnd: '',
        creators: [],
      });
      isAccurateSearch.value = true;
      methods.pullList();
    },
    switchSort(columnName: string) {
      // 升序→降序→无序→升序......
      if (!isAccurateSearch.value) {
        // 模糊搜索不排序
        return;
      }
      methods.sortChange(sortMethods.switchSort(columnName));
    },
    sortChange(newSort: Sort | undefined) {
      sortMethods.sortChange(newSort);
      methods.pullList();
    },
  };
  methods.initData();
  return {
    searchParams,
    searchCondition,
    SearchRangeOptions,
    SearchTypeOptions,
    SearchTimeOptions,
    SearchSizeOptions,
    SearchMemberOptions,
    columns,
    loading,
    isAccurateSearch,
    loadingStatus,
    tableData,
    sort: tableSort,
    methods,
  };
}
